import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTenant } from './TenantContext';
import workspaceApi, { WorkspaceDTO } from '../api/workspaces';
import { setTenantContext } from '../utils/axiosInterceptor';

interface WorkspaceContextType {
  // State
  workspaces: WorkspaceDTO[];
  currentWorkspace: WorkspaceDTO | null;
  loading: boolean;
  error: string | null;

  // Actions
  refreshWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => void;
  createWorkspace: (name: string, description?: string, isDefault?: boolean) => Promise<WorkspaceDTO>;
  updateWorkspace: (workspaceId: string, updates: Partial<WorkspaceDTO>) => Promise<WorkspaceDTO>;
  archiveWorkspace: (workspaceId: string) => Promise<void>;
  unarchiveWorkspace: (workspaceId: string) => Promise<void>;
  setDefaultWorkspace: (workspaceId: string) => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// Local storage key for persisting selected workspace
const WORKSPACE_STORAGE_KEY = 'enveu-current-workspace';

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const { currentTenant, loading: tenantLoading } = useTenant();
  
  const [workspaces, setWorkspaces] = useState<WorkspaceDTO[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to access currentWorkspace inside callbacks without adding it as a dependency
  const currentWorkspaceRef = React.useRef<WorkspaceDTO | null>(null);
  React.useEffect(() => {
    currentWorkspaceRef.current = currentWorkspace;
  }, [currentWorkspace]);

  /**
   * Fetch workspaces for the current tenant
   */
  const fetchWorkspaces = useCallback(async () => {
    if (!currentTenant?.tenantId) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const fetchedWorkspaces = await workspaceApi.getWorkspaces(currentTenant.tenantId);
      setWorkspaces(fetchedWorkspaces);

      // If there's already an active workspace and it still exists, preserve it (just refresh its data).
      // This prevents refreshWorkspaces() calls from resetting the user's current selection.
      const existing = currentWorkspaceRef.current;
      if (existing) {
        const stillValid = fetchedWorkspaces.find(ws => ws.workspaceId === existing.workspaceId);
        if (stillValid) {
          setCurrentWorkspace(stillValid);
          return;
        }
      }

      // Initial load — no current workspace yet: stored preference > default > first
      // Try to restore previously selected workspace from storage
      const storedWorkspaceId = localStorage.getItem(
        `${WORKSPACE_STORAGE_KEY}-${currentTenant.tenantId}`
      );

      // Find workspace to select: stored preference > default > first
      let selectedWorkspace: WorkspaceDTO | null = null;

      if (storedWorkspaceId) {
        selectedWorkspace = fetchedWorkspaces.find(
          ws => ws.workspaceId === storedWorkspaceId
        ) || null;
      }

      if (!selectedWorkspace) {
        selectedWorkspace = fetchedWorkspaces.find(ws => ws.isDefault) || null;
      }

      if (!selectedWorkspace && fetchedWorkspaces.length > 0) {
        selectedWorkspace = fetchedWorkspaces[0];
      }

      setCurrentWorkspace(selectedWorkspace);

      console.log('Workspaces loaded:', {
        tenantId: currentTenant.tenantId,
        count: fetchedWorkspaces.length,
        currentWorkspace: selectedWorkspace?.workspaceId,
      });
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  }, [currentTenant?.tenantId]);

  /**
   * Refresh workspaces
   */
  const refreshWorkspaces = useCallback(async () => {
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  /**
   * Switch to a different workspace
   */
  const switchWorkspace = useCallback((workspaceId: string) => {
    const workspace = workspaces.find(ws => ws.workspaceId === workspaceId);
    if (workspace) {
      setCurrentWorkspace(workspace);
      
      // Persist selection
      if (currentTenant?.tenantId) {
        localStorage.setItem(
          `${WORKSPACE_STORAGE_KEY}-${currentTenant.tenantId}`,
          workspaceId
        );
      }

      console.log('Switched to workspace:', workspaceId);
    }
  }, [workspaces, currentTenant?.tenantId]);

  /**
   * Create a new workspace
   */
  const createWorkspace = useCallback(async (
    name: string,
    description?: string,
    isDefault?: boolean
  ): Promise<WorkspaceDTO> => {
    if (!currentTenant?.tenantId) {
      throw new Error('No tenant selected');
    }

    const newWorkspace = await workspaceApi.createWorkspace(currentTenant.tenantId, {
      name,
      description,
      isDefault,
    });

    // Refresh workspace list
    await fetchWorkspaces();

    return newWorkspace;
  }, [currentTenant?.tenantId, fetchWorkspaces]);

  /**
   * Update a workspace
   */
  const updateWorkspace = useCallback(async (
    workspaceId: string,
    updates: Partial<WorkspaceDTO>
  ): Promise<WorkspaceDTO> => {
    if (!currentTenant?.tenantId) {
      throw new Error('No tenant selected');
    }

    const updatedWorkspace = await workspaceApi.updateWorkspace(
      currentTenant.tenantId,
      workspaceId,
      {
        name: updates.name,
        description: updates.description,
        icon: updates.icon,
        color: updates.color,
        settings: updates.settings,
        status: updates.status,
      }
    );

    // Update local state
    setWorkspaces(prev => 
      prev.map(ws => ws.workspaceId === workspaceId ? updatedWorkspace : ws)
    );

    if (currentWorkspace?.workspaceId === workspaceId) {
      setCurrentWorkspace(updatedWorkspace);
    }

    return updatedWorkspace;
  }, [currentTenant?.tenantId, currentWorkspace?.workspaceId]);

  /**
   * Archive a workspace
   */
  const archiveWorkspace = useCallback(async (workspaceId: string): Promise<void> => {
    if (!currentTenant?.tenantId) {
      throw new Error('No tenant selected');
    }

    await workspaceApi.archiveWorkspace(currentTenant.tenantId, workspaceId);

    // Refresh workspace list
    await fetchWorkspaces();

    // If the archived workspace was selected, switch to default
    if (currentWorkspace?.workspaceId === workspaceId) {
      const defaultWorkspace = workspaces.find(ws => ws.isDefault);
      if (defaultWorkspace) {
        setCurrentWorkspace(defaultWorkspace);
      }
    }
  }, [currentTenant?.tenantId, currentWorkspace?.workspaceId, workspaces, fetchWorkspaces]);

  /**
   * Unarchive a workspace (restore from archive)
   */
  const unarchiveWorkspace = useCallback(async (workspaceId: string): Promise<void> => {
    if (!currentTenant?.tenantId) {
      throw new Error('No tenant selected');
    }

    await workspaceApi.unarchiveWorkspace(currentTenant.tenantId, workspaceId);

    // Refresh workspace list
    await fetchWorkspaces();
  }, [currentTenant?.tenantId, fetchWorkspaces]);

  /**
   * Set a workspace as the default
   */
  const setDefaultWorkspace = useCallback(async (workspaceId: string): Promise<void> => {
    if (!currentTenant?.tenantId) {
      throw new Error('No tenant selected');
    }

    await workspaceApi.setDefaultWorkspace(currentTenant.tenantId, workspaceId);

    // Refresh workspace list to get updated default status
    await fetchWorkspaces();
  }, [currentTenant?.tenantId, fetchWorkspaces]);

  // Fetch workspaces when tenant changes
  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      fetchWorkspaces();
    } else if (!tenantLoading && !currentTenant) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setLoading(false);
    }
  }, [currentTenant, tenantLoading, fetchWorkspaces]);

  // Sync current workspace with axios interceptors
  useEffect(() => {
    if (currentTenant?.tenantId && currentWorkspace?.workspaceId) {
      setTenantContext(currentTenant.tenantId, currentWorkspace.workspaceId);
      console.log('Synced workspace context to axios:', {
        tenantId: currentTenant.tenantId,
        workspaceId: currentWorkspace.workspaceId,
      });
    } else if (currentTenant?.tenantId) {
      // If tenant exists but no workspace selected, don't send workspace header
      setTenantContext(currentTenant.tenantId, null);
    }
  }, [currentTenant?.tenantId, currentWorkspace?.workspaceId]);

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    loading,
    error,
    refreshWorkspaces,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    archiveWorkspace,
    unarchiveWorkspace,
    setDefaultWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * Hook to access workspace context
 */
export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export default WorkspaceContext;
